import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import {
  X,
  Check,
  Folder,
  Layout,
  Database,
  Code,
  Terminal,
  Search,
  Server,
  ChevronsDown,
  ChevronsUp,
  AlertCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { datasetApi } from '../../api/datasetApi';
import { udfApi } from '../../api/udfApi';
import { useAuth } from '../../context/AuthContext';
import { FEATURE_INTEGRATION_UDF } from '../../constants/capabilities';
import {
  MAX_DATASETS_PER_WORKSPACE,
  MAX_FUNCTIONS_PER_WORKSPACE
} from '../../constants/limits';
import {
  InventoryTreeNode,
  buildSortedDatasetTree,
  buildSortedFunctionTree,
  collectDatasetTreeNodeIds,
  collectFunctionTreeNodeIds,
  DatasetInventoryTree,
  datasetLeafItem,
  datasetLeafIcon,
  filterDatasetTree,
  hasManteoDatasets,
} from './inventoryTreeShared';

const CreateWorkspaceWizard = ({ isOpen, onClose, onCreated }) => {
  const DEFAULT_WORKSPACE_NAME = 'Untitled Workspace';
  const { capabilities, user } = useAuth();
  const userFolderLabel = user?.email || 'My data';
  const udfEnabled = capabilities?.features?.[FEATURE_INTEGRATION_UDF] !== false;
  const [formData, setFormData] = useState({
    name: DEFAULT_WORKSPACE_NAME,
    description: '',
    selectedSources: [],
    selectedUDFs: []
  });
  const [availableDatasets, setAvailableDatasets] = useState([]);
  const [availableUDFs, setAvailableUDFs] = useState([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [isLoadingUDFs, setIsLoadingUDFs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [functionSearchQuery, setFunctionSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const datasetTreeExpandedOnceRef = useRef(false);
  const functionTreeExpandedOnceRef = useRef(false);
  const [inlineError, setInlineError] = useState(null);
  const [nameRequiredError, setNameRequiredError] = useState(false);
  const [selectionTab, setSelectionTab] = useState('datasets'); // 'datasets' | 'functions'
  const nameInputRef = useRef(null);

  const loadDatasets = useCallback(async (silent = false) => {
    if (!silent) setIsLoadingDatasets(true);
    try {
      const data = await datasetApi.getDatasets();
      setAvailableDatasets(data);
    } catch (err) {
      console.error('Failed to load datasets:', err);
    } finally {
      if (!silent) setIsLoadingDatasets(false);
    }
  }, []);

  const loadUDFs = useCallback(async () => {
    if (!udfEnabled) {
      setAvailableUDFs([]);
      setIsLoadingUDFs(false);
      return;
    }

    setIsLoadingUDFs(true);
    try {
      const data = await udfApi.listUDFs();
      setAvailableUDFs(data);
    } catch (err) {
      console.error('Failed to load UDFs:', err);
    } finally {
      setIsLoadingUDFs(false);
    }
  }, [udfEnabled]);

  // Reset state when wizard closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({ name: DEFAULT_WORKSPACE_NAME, description: '', selectedSources: [], selectedUDFs: [] });
      setSearchQuery('');
      setFunctionSearchQuery('');
      setSelectionTab('datasets');
      setInlineError(null);
      setNameRequiredError(false);
      setExpandedNodes(new Set());
      datasetTreeExpandedOnceRef.current = false;
      functionTreeExpandedOnceRef.current = false;
    } else {
      loadDatasets();
      loadUDFs();
    }
  }, [isOpen, loadDatasets, loadUDFs, DEFAULT_WORKSPACE_NAME]);

  // Background polling for status updates
  React.useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      loadDatasets(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, loadDatasets]);

  const datasetsTree = useMemo(
    () => buildSortedDatasetTree(availableDatasets),
    [availableDatasets]
  );

  const functionsTree = useMemo(
    () => buildSortedFunctionTree(availableUDFs),
    [availableUDFs]
  );

  const filteredDatasetsTree = useMemo(
    () => filterDatasetTree(datasetsTree, searchQuery),
    [datasetsTree, searchQuery]
  );

  // Filter functions based on search
  const filteredFunctionsTree = useMemo(() => {
    return {
      bigquery: Object.fromEntries(
        Object.entries(functionsTree.bigquery).map(([projectId, datasets]) => {
          const filteredDatasets = Object.fromEntries(
            Object.entries(datasets).map(([datasetId, items]) => [
              datasetId,
              items.filter(item => {
                const q = functionSearchQuery.toLowerCase();
                return (item.functionName || item.name || "").toLowerCase().includes(q) ||
                  (item.signature || "").toLowerCase().includes(q) ||
                  (item.description || "").toLowerCase().includes(q);
              })
            ]).filter(([_, items]) => items.length > 0)
          );
          return [projectId, filteredDatasets];
        }).filter(([_, datasets]) => Object.keys(datasets).length > 0)
      ),
      custom: functionsTree.custom.filter(item => {
        const q = functionSearchQuery.toLowerCase();
        return (item.name || "").toLowerCase().includes(q) ||
          (item.signature || "").toLowerCase().includes(q) ||
          (item.description || "").toLowerCase().includes(q);
      })
    };
  }, [functionsTree, functionSearchQuery]);

  const includeFunctionsStep = udfEnabled;

  // Expand each section once when its inventory first loads (not on background poll)
  useEffect(() => {
    if (!isOpen || datasetTreeExpandedOnceRef.current || isLoadingDatasets) return;
    const datasetIds = collectDatasetTreeNodeIds(datasetsTree);
    if (datasetIds.size === 0) return;
    setExpandedNodes((prev) => new Set([...prev, ...datasetIds]));
    datasetTreeExpandedOnceRef.current = true;
  }, [isOpen, datasetsTree, isLoadingDatasets]);

  useEffect(() => {
    if (!isOpen || !includeFunctionsStep || functionTreeExpandedOnceRef.current || isLoadingUDFs) return;
    const functionIds = collectFunctionTreeNodeIds(functionsTree);
    if (functionIds.size === 0) return;
    setExpandedNodes((prev) => new Set([...prev, ...functionIds]));
    functionTreeExpandedOnceRef.current = true;
  }, [isOpen, functionsTree, isLoadingUDFs, includeFunctionsStep]);

  if (!isOpen) return null;

  const toggleNode = (nodeId) => {
    const next = new Set(expandedNodes);
    next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
    setExpandedNodes(next);
  };

  const expandAll = (section = 'datasets') => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      const sectionIds = section === 'datasets'
        ? collectDatasetTreeNodeIds(datasetsTree)
        : collectFunctionTreeNodeIds(functionsTree);
      sectionIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const toggleUDF = (udf) => {
    setFormData(prev => {
      const exists = prev.selectedUDFs.find(u => u.id === udf.id);
      if (exists) {
        return { ...prev, selectedUDFs: prev.selectedUDFs.filter(u => u.id !== udf.id) };
      }
      if (prev.selectedUDFs.length >= MAX_FUNCTIONS_PER_WORKSPACE) {
        setInlineError(`A workspace can contain at most ${MAX_FUNCTIONS_PER_WORKSPACE} functions.`);
        return prev;
      }
      setInlineError(null);
      return { ...prev, selectedUDFs: [...prev.selectedUDFs, udf] };
    });
  };

  const toggleSource = (source) => {
    setFormData(prev => {
      const exists = prev.selectedSources.find(s => s.id === source.id);
      if (exists) {
        return { ...prev, selectedSources: prev.selectedSources.filter(s => s.id !== source.id) };
      }

      // If we are adding a new source, check if it matches the connector type of already selected sources
      const currentSelectedType = prev.selectedSources.length > 0 ? (
        prev.selectedSources[0].type === 'bigquery' ? 'bigquery' : 'local'
      ) : null;

      const newSourceType = source.type === 'bigquery' ? 'bigquery' : 'local';

      if (currentSelectedType && currentSelectedType !== newSourceType) {
        setInlineError(
          `Workspaces can only contain datasets of the same type. This workspace is already configured for ${currentSelectedType} sources.`
        );
        return prev;
      }
      if (prev.selectedSources.length >= MAX_DATASETS_PER_WORKSPACE) {
        setInlineError(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
        return prev;
      }

      setInlineError(null);
      return { ...prev, selectedSources: [...prev.selectedSources, source] };
    });
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setNameRequiredError(true);
      nameInputRef.current?.focus();
      return;
    }
    if (formData.selectedSources.length > MAX_DATASETS_PER_WORKSPACE) {
      setInlineError(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
      return;
    }
    if (formData.selectedUDFs.length > MAX_FUNCTIONS_PER_WORKSPACE) {
      setInlineError(`A workspace can contain at most ${MAX_FUNCTIONS_PER_WORKSPACE} functions.`);
      return;
    }

    const payload = {
      name: formData.name,
      description: formData.description,
      sources: formData.selectedSources.map(s => s.id)
    };

    if (includeFunctionsStep && formData.selectedUDFs.length > 0) {
      payload.udf_ids = formData.selectedUDFs.map(u => u.id);
    }

    try {
      await onCreated(payload);
      onClose();
      // Reset wizard
      setFormData({ name: DEFAULT_WORKSPACE_NAME, description: '', selectedSources: [], selectedUDFs: [] });
      setNameRequiredError(false);
      setInlineError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setInlineError(detail || err?.message || 'Failed to create workspace. Please try again.');
    }
  };

  const hasDatasets = hasManteoDatasets(filteredDatasetsTree.manteo) || Object.keys(filteredDatasetsTree.bigquery).length > 0;
  const hasFunctions = Object.keys(filteredFunctionsTree.bigquery).length > 0 || filteredFunctionsTree.custom.length > 0;

  const SectionHeader = ({ icon: Icon, title, description, count, colorClass = 'text-emerald-600' }) => (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2.5">
        <div className={cn("mt-0.5 rounded-2xl bg-gray-50 p-2 dark:bg-gray-900", colorClass)}>
          <Icon size={16} strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <h4 className="text-[11px] font-black uppercase tracking-[0.18em] text-gray-900 dark:text-white">{title}</h4>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-gray-400">{description}</p>
        </div>
      </div>
      {count !== undefined && (
        <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-black text-gray-500 dark:bg-gray-900 dark:text-gray-400">
          {count}
        </span>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative flex max-h-[86vh] w-full max-w-6xl flex-col overflow-hidden rounded-[32px] border border-gray-100 bg-white shadow-2xl animate-in zoom-in-95 duration-200 dark:border-gray-800 dark:bg-gray-950">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-gray-800">
          <div>
            <h3 className="text-lg font-black uppercase tracking-tight text-gray-900 dark:text-white">
              Create Workspace
            </h3>
            <p className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Select datasets to include, then name your workspace
            </p>
          </div>
          <button onClick={onClose} className="rounded-2xl bg-gray-50 p-2.5 text-gray-400 transition-all hover:text-gray-900 dark:bg-gray-900 dark:hover:text-white">
            <X size={18} />
          </button>
        </div>

        {inlineError && (
          <div className="mx-6 mt-4 flex shrink-0 items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-900/20">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
            <p className="flex-1 text-xs font-bold text-red-600 dark:text-red-300">{inlineError}</p>
            <button
              type="button"
              onClick={() => setInlineError(null)}
              className="shrink-0 rounded-lg p-1 transition-colors hover:bg-red-100 dark:hover:bg-red-800"
              aria-label="Dismiss"
            >
              <X size={14} className="text-red-400" />
            </button>
          </div>
        )}

        <div className="grid flex-1 gap-4 overflow-hidden p-6 lg:grid-cols-[0.85fr_minmax(0,1.75fr)]">
          <section className="flex min-h-0 flex-col rounded-[28px] bg-gray-50/50 p-3 dark:bg-gray-900/30">
            <SectionHeader
              icon={Layout}
              title="Details"
              description="Name required"
            />

            <div className="mt-3 space-y-2">
              <div className="space-y-1">
                <label htmlFor="workspace-wizard-name" className="text-[8px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="workspace-wizard-name"
                  ref={nameInputRef}
                  autoFocus
                  className={cn(
                    'w-full rounded-lg border bg-white px-2.5 py-2 text-sm font-bold text-gray-900 outline-none transition-all dark:bg-gray-900 dark:text-white',
                    'border-gray-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/25 dark:border-gray-600 dark:focus:border-emerald-500',
                    nameRequiredError && 'border-red-400 focus:border-red-500 focus:ring-red-500/20 dark:border-red-500'
                  )}
                  placeholder="e.g. Sales Analysis Q4"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (nameRequiredError && e.target.value.trim()) {
                      setNameRequiredError(false);
                    }
                  }}
                />
                {nameRequiredError && (
                  <p className="text-[10px] font-bold text-red-500">Name is required.</p>
                )}
              </div>
              <div className="space-y-1">
                <label htmlFor="workspace-wizard-description" className="text-[8px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Description <span className="font-medium normal-case tracking-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="workspace-wizard-description"
                  rows={2}
                  className="w-full resize-none rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm font-medium text-gray-900 outline-none transition-all focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/25 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-emerald-500"
                  placeholder="Optional context"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-2 min-h-0 flex-1 p-1">
              <span className="text-[8px] font-black uppercase tracking-widest text-gray-400">Datasets</span>
              {formData.selectedSources.length > 0 ? (
                <div className="mt-1.5 flex max-h-28 flex-wrap gap-1 overflow-y-auto custom-scrollbar">
                  {formData.selectedSources.map(s => (
                    <div key={s.id} className="flex max-w-full items-center gap-1 rounded-md bg-gray-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-tight text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      <Check size={8} strokeWidth={3} className="shrink-0 text-gray-500 dark:text-gray-400" />
                      <span className="truncate">{s.name || s.tableName || s.id}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-[11px] font-medium text-gray-400">None selected yet — use the checkboxes on the right.</p>
              )}
            </div>
          </section>

          <section className="flex min-h-0 flex-col rounded-[28px] p-4 dark:bg-gray-950">
            <div className="mb-4 space-y-2">
              <div className="flex shrink-0 gap-1 rounded-2xl bg-gray-50/80 p-1 dark:bg-gray-900/50">
                {includeFunctionsStep ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setSelectionTab('datasets')}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-wider transition-all',
                        selectionTab === 'datasets'
                          ? 'bg-white text-emerald-600 shadow-sm dark:bg-gray-950 dark:text-emerald-400'
                          : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                      )}
                    >
                      <Database size={14} className="shrink-0" />
                      Datasets
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-black tabular-nums text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        {formData.selectedSources.length}/{MAX_DATASETS_PER_WORKSPACE}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectionTab('functions')}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-wider transition-all',
                        selectionTab === 'functions'
                          ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-950 dark:text-blue-400'
                          : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                      )}
                    >
                      <Code size={14} className="shrink-0" />
                      Functions
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-black tabular-nums text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        {formData.selectedUDFs.length}/{MAX_FUNCTIONS_PER_WORKSPACE}
                      </span>
                    </button>
                  </>
                ) : (
                  <div
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-wider bg-white text-emerald-600 shadow-sm dark:bg-gray-950 dark:text-emerald-400"
                    role="status"
                    aria-label="Datasets selection"
                  >
                    <Database size={14} className="shrink-0" />
                    Datasets
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-black tabular-nums text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                      {formData.selectedSources.length}/{MAX_DATASETS_PER_WORKSPACE}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {(!includeFunctionsStep || selectionTab === 'datasets') && (
              <>
                <div className="mb-3 flex items-start gap-2.5 rounded-2xl bg-emerald-50/70 px-3 py-2.5 dark:bg-emerald-950/25">
                  <Database size={15} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <p className="text-xs font-medium leading-snug text-emerald-900 dark:text-emerald-100">
                    Select one or more datasets to include in this workspace. You can add or remove datasets later.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <div className="group relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-emerald-500" size={15} />
                    <input
                      type="text"
                      placeholder="Search datasets"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full rounded-2xl border border-transparent bg-gray-50 py-2.5 pl-9 pr-3 text-xs font-bold outline-none transition-all focus:border-emerald-500/50 dark:bg-gray-900"
                    />
                  </div>
                  {hasDatasets && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => expandAll('datasets')}
                        className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-emerald-600 dark:hover:bg-gray-900"
                        title="Expand datasets"
                      >
                        <ChevronsDown size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={collapseAll}
                        className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-emerald-600 dark:hover:bg-gray-900"
                        title="Collapse datasets"
                      >
                        <ChevronsUp size={15} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-2xl bg-gray-50/40 p-2 custom-scrollbar dark:bg-gray-900/30">
                  {isLoadingDatasets ? (
                    <div className="flex h-full min-h-44 flex-col items-center justify-center gap-3">
                      <div className="h-7 w-7 rounded-full border-4 border-emerald-500/20 border-t-emerald-500 animate-spin" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Scanning resources...</span>
                    </div>
                  ) : hasDatasets ? (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800/80">
                      <DatasetInventoryTree
                        tree={filteredDatasetsTree}
                        userFolderLabel={userFolderLabel}
                        expandedNodes={expandedNodes}
                        onToggleNode={toggleNode}
                        renderDatasetLeaf={(item, level) => {
                          const isSelected = formData.selectedSources.find(s => s.id === item.id);
                          const currentSelectedType = formData.selectedSources.length > 0 ? (
                            formData.selectedSources[0].type === 'bigquery' ? 'bigquery' : 'local'
                          ) : null;
                          const thisSourceType = item.type === 'bigquery' ? 'bigquery' : 'local';
                          const isTypeDisabled = currentSelectedType && currentSelectedType !== thisSourceType;
                          const isStatusDisabled = item.status && item.status !== 'ready';
                          const isDisabled = isTypeDisabled || isStatusDisabled;
                          const isBigQuery = item.type === 'bigquery';
                          return (
                            <InventoryTreeNode
                              key={item.id}
                              item={datasetLeafItem(item)}
                              level={level}
                              onSelect={() => !isDisabled && toggleSource(item)}
                              icon={datasetLeafIcon}
                              iconColor={isBigQuery ? 'text-blue-500/60' : 'text-emerald-500/70'}
                              isSelected={!!isSelected}
                              isDisabled={isDisabled}
                              useCheckbox
                            />
                          );
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex h-full min-h-44 items-center justify-center text-center">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">No datasets found</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {includeFunctionsStep && selectionTab === 'functions' && (
              <>
                <div className="flex items-center gap-2">
                  <div className="group relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-emerald-500" size={15} />
                    <input
                      type="text"
                      placeholder="Search functions"
                      value={functionSearchQuery}
                      onChange={(e) => setFunctionSearchQuery(e.target.value)}
                      className="w-full rounded-2xl border border-transparent bg-gray-50 py-2.5 pl-9 pr-3 text-xs font-bold outline-none transition-all focus:border-emerald-500/50 dark:bg-gray-900"
                    />
                  </div>
                  {hasFunctions && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => expandAll('functions')}
                        className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-emerald-600 dark:hover:bg-gray-900"
                        title="Expand functions"
                      >
                        <ChevronsDown size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={collapseAll}
                        className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-emerald-600 dark:hover:bg-gray-900"
                        title="Collapse functions"
                      >
                        <ChevronsUp size={15} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-2xl bg-gray-50/40 p-2 custom-scrollbar dark:bg-gray-900/30">
                  {isLoadingUDFs ? (
                    <div className="flex h-full min-h-44 flex-col items-center justify-center gap-3">
                      <div className="h-7 w-7 rounded-full border-4 border-emerald-500/20 border-t-emerald-500 animate-spin" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Loading functions...</span>
                    </div>
                  ) : hasFunctions ? (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800/80">
                      {Object.keys(filteredFunctionsTree.bigquery).length > 0 && (
                        <InventoryTreeNode
                          item={{ id: 'bigquery', name: 'BigQuery' }}
                          level={0}
                          isExpanded={expandedNodes.has('bigquery')}
                          onToggle={() => toggleNode('bigquery')}
                          icon={Server}
                          iconColor="text-blue-500"
                          children={
                            <div>
                              {Object.entries(filteredFunctionsTree.bigquery).map(([projectId, datasets]) => (
                                <InventoryTreeNode
                                  key={projectId}
                                  item={{ id: projectId, name: projectId }}
                                  level={1}
                                  isExpanded={expandedNodes.has(projectId)}
                                  onToggle={() => toggleNode(projectId)}
                                  icon={Folder}
                                  iconColor="text-blue-500"
                                  children={
                                    <div>
                                      {Object.entries(datasets).map(([datasetId, items]) => (
                                        <InventoryTreeNode
                                          key={datasetId}
                                          item={{ id: datasetId, name: datasetId }}
                                          level={2}
                                          isExpanded={expandedNodes.has(datasetId)}
                                          onToggle={() => toggleNode(datasetId)}
                                          icon={Folder}
                                          iconColor="text-blue-400"
                                          children={
                                            <div>
                                              {items.map(item => {
                                                const isSelected = formData.selectedUDFs.find(u => u.id === item.id);
                                                return (
                                                  <InventoryTreeNode
                                                    key={item.id}
                                                    item={{
                                                      id: item.id,
                                                      name: item.functionName || item.name,
                                                      signature: item.signature,
                                                      created_at: item.created_at,
                                                    }}
                                                    level={3}
                                                    onSelect={() => toggleUDF(item)}
                                                    icon={Terminal}
                                                    iconColor="text-blue-500/60"
                                                    isSelected={!!isSelected}
                                                  />
                                                );
                                              })}
                                            </div>
                                          }
                                        />
                                      ))}
                                    </div>
                                  }
                                />
                              ))}
                            </div>
                          }
                        />
                      )}

                      {filteredFunctionsTree.custom.length > 0 && (
                        <InventoryTreeNode
                          item={{ id: 'custom', name: 'Custom functions' }}
                          level={0}
                          isExpanded={expandedNodes.has('custom')}
                          onToggle={() => toggleNode('custom')}
                          icon={Folder}
                          iconColor="text-emerald-500"
                          children={
                            <div>
                              {filteredFunctionsTree.custom.map(item => {
                                const isSelected = formData.selectedUDFs.find(u => u.id === item.id);
                                return (
                                  <InventoryTreeNode
                                    key={item.id}
                                    item={{
                                      id: item.id,
                                      name: item.name,
                                      signature: item.signature,
                                      created_at: item.created_at,
                                    }}
                                    level={1}
                                    onSelect={() => toggleUDF(item)}
                                    icon={Terminal}
                                    iconColor="text-emerald-500/70"
                                    isSelected={!!isSelected}
                                  />
                                );
                              })}
                            </div>
                          }
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex h-full min-h-44 flex-col items-center justify-center text-center">
                      <Code size={26} className="mb-2 text-gray-300 dark:text-gray-700" />
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">No functions available</p>
                      <p className="mt-1 text-[9px] text-gray-400">You can add functions later.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-gray-100 bg-gray-50/60 px-6 py-4 dark:border-gray-800 dark:bg-gray-900/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 transition-all hover:text-gray-900 dark:hover:text-white"
          >
            Cancel
          </button>

          <button
            onClick={handleCreate}
            className="flex items-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-lg shadow-emerald-600/20 transition-all hover:bg-emerald-700"
          >
            <Check size={15} strokeWidth={3} />
            Create Workspace
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkspaceWizard;
